import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  UpdateExternalNetworkDto,
  UpsertSurfaceProfileDto,
} from '@terra/shared-contracts-external-network';
import type { UpdateInternalNetworkDto } from '@terra/shared-contracts-internal-network';
import { terraProxyApiClient } from '../../clients/testerClient';
import { useProjectStore } from '../../store/projectStore';
import {
  mapExternalNetworkToPortalTarget,
  mapInternalNetworkToPortalTarget,
} from '../../utils/portal-target.utils';
import { TARGETS_QUERY_KEY } from '../useTargets';

export const INTERNAL_NETWORK_EXTERNAL_NETWORK_DETAIL_KEY =
  'internal-network-surface-detail';
export const EXTERNAL_NETWORK_DETAIL_KEY = 'external-network-detail';
export const EXTERNAL_NETWORK_PROFILE_KEY = 'external-network-profile';
export const INTERNAL_NETWORK_ENROLLMENTS_KEY = 'internal-network-enrollments';
export const INTERNAL_NETWORK_CREDENTIALS_KEY = 'internal-network-credentials';

export function useInternalNetworkDetail(networkId: string | null) {
  return useQuery({
    queryKey: [INTERNAL_NETWORK_EXTERNAL_NETWORK_DETAIL_KEY, networkId],
    queryFn: () => terraProxyApiClient.getInternalNetwork(networkId ?? ''),
    enabled: Boolean(networkId),
  });
}

export function useUpdateInternalNetwork(networkId: string | null) {
  const queryClient = useQueryClient();
  const updateSelectedTarget = useProjectStore(
    (state) => state.updateSelectedTarget,
  );

  return useMutation({
    mutationFn: (body: UpdateInternalNetworkDto) => {
      if (!networkId) {
        throw new Error('Network ID is required');
      }

      return terraProxyApiClient.updateInternalNetwork({
        networkId,
        body,
      });
    },
    onSuccess: (surface) => {
      queryClient.setQueryData(
        [INTERNAL_NETWORK_EXTERNAL_NETWORK_DETAIL_KEY, networkId],
        surface,
      );
      updateSelectedTarget(
        mapInternalNetworkToPortalTarget({
          ...surface,
          signalCategoriesInScope: surface.signalCategoriesInScope ?? [],
          smokeTestEnabled: surface.smokeTestEnabled ?? false,
        }),
      );
      void queryClient.invalidateQueries({ queryKey: [TARGETS_QUERY_KEY] });
    },
  });
}

export function useDeleteInternalNetwork() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (networkId: string) =>
      terraProxyApiClient.deleteInternalNetwork(networkId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [TARGETS_QUERY_KEY] });
    },
  });
}

export function useInternalNetworkEnrollments(networkId: string | null) {
  return useQuery({
    queryKey: [INTERNAL_NETWORK_ENROLLMENTS_KEY, networkId],
    queryFn: () =>
      terraProxyApiClient.listInternalNetworkEnrollments(networkId ?? ''),
    enabled: Boolean(networkId),
  });
}

export function useInternalNetworkCredentials(networkId: string | null) {
  return useQuery({
    queryKey: [INTERNAL_NETWORK_CREDENTIALS_KEY, networkId],
    queryFn: () =>
      terraProxyApiClient.listInternalNetworkCredentials(networkId ?? ''),
    enabled: Boolean(networkId),
  });
}

export function useExternalNetworkSurfaceDetail(surfaceId: string | null) {
  return useQuery({
    queryKey: [EXTERNAL_NETWORK_DETAIL_KEY, surfaceId],
    queryFn: () => terraProxyApiClient.getExternalNetwork(surfaceId ?? ''),
    enabled: Boolean(surfaceId),
  });
}

export function useUpdateExternalNetworkSurface(surfaceId: string | null) {
  const queryClient = useQueryClient();
  const updateSelectedTarget = useProjectStore(
    (state) => state.updateSelectedTarget,
  );

  return useMutation({
    mutationFn: (body: UpdateExternalNetworkDto) => {
      if (!surfaceId) {
        throw new Error('Surface ID is required');
      }

      return terraProxyApiClient.updateExternalNetwork({ surfaceId, body });
    },
    onSuccess: (surface) => {
      queryClient.setQueryData(
        [EXTERNAL_NETWORK_DETAIL_KEY, surfaceId],
        surface,
      );
      updateSelectedTarget(mapExternalNetworkToPortalTarget(surface));
      void queryClient.invalidateQueries({ queryKey: [TARGETS_QUERY_KEY] });
    },
  });
}

export function useDeleteExternalNetworkSurface() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (surfaceId: string) =>
      terraProxyApiClient.deleteExternalNetwork(surfaceId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [TARGETS_QUERY_KEY] });
    },
  });
}

export function useExternalNetworkProfile(surfaceId: string | null) {
  return useQuery({
    queryKey: [EXTERNAL_NETWORK_PROFILE_KEY, surfaceId],
    queryFn: () =>
      terraProxyApiClient.getExternalNetworkProfile(surfaceId ?? ''),
    enabled: Boolean(surfaceId),
  });
}

export function useUpsertExternalNetworkProfile(surfaceId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: UpsertSurfaceProfileDto) => {
      if (!surfaceId) {
        throw new Error('Surface ID is required');
      }

      return terraProxyApiClient.upsertExternalNetworkProfile({
        surfaceId,
        body,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [EXTERNAL_NETWORK_PROFILE_KEY, surfaceId],
      });
    },
  });
}
